import {defineStore} from 'pinia'
import {api} from '@/services/api'
import pako from 'pako'
import {useAuthStore} from "@/stores/authStore.js";
import {permissionService} from "@/services/PermissionService";
import {decompressAndParse as wasmDecompressAndParse, decompressAndParseJS, preloadWasm} from "@/services/wasmService.js";
import {passengerIndex} from "@/services/PassengerIndex.js";

// Track if we've set up the service worker listener
let swListenerSetup = false;

// Utility function for adding retry functionality
const fetchWithRetry = async (fetchFunc, retryCount = 3, delay = 1000) => {
    let lastError;

    for (let attempt = 0; attempt < retryCount; attempt++) {
        try {
            const result = await fetchFunc();
            return result; // Success, return the result
        } catch (error) {
            console.warn(`Attempt ${attempt + 1}/${retryCount} failed:`, error);
            lastError = error;

            if (attempt < retryCount - 1) {
                // Wait before next retry with exponential backoff
                await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, attempt)));
            }
        }
    }

    // All retries failed, throw the last error
    throw lastError;
};

export const useWeekStore = defineStore('week', {
    state: () => ({
        selectedWeek: null,
        isLoading: false,
        weekData: null,
        repData: null,
        weekName: null,
        systemId: null,
        weekId: null,
        repObj: null,
        error: null,
        retryCount: 0,
        isStaleData: false,  // Indicates if current data is from stale cache
        lastDataUpdate: null, // Timestamp of last data update
        lastDecompressMetrics: null, // Performance metrics from last decompression
        useWasm: true // Feature flag to enable/disable WASM (can be toggled for debugging)
    }),
    actions: {
        setSelectedWeek(week) {
            this.selectedWeek = week
        },
        setLoading(status) {
            this.isLoading = status
        },
        /**
         * Set week data and build passenger indexes for O(1) lookups
         * @param {Array} data - Array of passenger objects
         */
        setWeekData(data) {
            this.weekData = data;
            // Build indexes for fast lookups
            if (data && Array.isArray(data)) {
                passengerIndex.buildIndexes(data);
            }
        },
        /**
         * JavaScript fallback for decompression (original implementation)
         * This is the tried-and-tested code that always works
         */
        async decompressDataJS(compressedData) {
            // Check if we're on iOS 12 or lower
            const isOldiOS = /iPhone OS (?:9|10|11|12)_/.test(window.navigator.userAgent);

            // Use modern API if available and not on old iOS
            if (!isOldiOS && typeof DecompressionStream !== 'undefined') {
                try {
                    const ds = new DecompressionStream('gzip');
                    const compressedStream = new Response(compressedData).body;

                    if (!compressedStream) {
                        throw new Error('Failed to create stream from response');
                    }

                    const decompressedStream = compressedStream.pipeThrough(ds);
                    const decompressedResponse = new Response(decompressedStream);
                    const decompressedText = await decompressedResponse.text();

                    return JSON.parse(decompressedText);
                } catch (error) {
                    console.error('Modern decompression failed, falling back to pako:', error);
                }
            }

            // Fallback for iOS 12 and other older browsers using pako
            let compressedArray;
            if (compressedData instanceof Uint8Array) {
                compressedArray = compressedData;
            } else if (compressedData instanceof ArrayBuffer) {
                compressedArray = new Uint8Array(compressedData);
            } else {
                throw new Error('Unsupported compressed data format');
            }

            const decompressedArray = pako.inflate(compressedArray);
            const decompressedText = new TextDecoder().decode(decompressedArray);
            return JSON.parse(decompressedText);
        },

        /**
         * Decompress and parse gzip-compressed JSON data
         * Uses WASM for performance when available, falls back to JavaScript
         */
        async decompressData(compressedData) {
            // If WASM is disabled via feature flag, use JS directly with metrics
            if (!this.useWasm) {
                const { data, metrics } = await decompressAndParseJS(compressedData);
                this.lastDecompressMetrics = metrics;
                return data;
            }

            try {
                // Try WASM with JS fallback
                const { data, metrics } = await wasmDecompressAndParse(compressedData, {
                    jsFallback: (data) => this.decompressDataJS(data)
                });

                // Store metrics for debugging/monitoring
                this.lastDecompressMetrics = metrics;

                return data;
            } catch (error) {
                // If everything fails, try pure JS one more time
                console.warn('WASM decompression failed, attempting pure JS fallback:', error);
                try {
                    const { data, metrics } = await decompressAndParseJS(compressedData);
                    this.lastDecompressMetrics = metrics;
                    return data;
                } catch (jsError) {
                    console.error('All decompression methods failed:', jsError);
                    throw new Error(`Failed to decompress data: ${jsError.message}`);
                }
            }
        },
        async loadWeekData(week) {
            this.setLoading(true)
            this.error = null
            this.retryCount = 0
            this.isStaleData = false

            // Set up service worker message listener for background updates
            this.setupServiceWorkerListener()

            try {
                const weekStore = JSON.stringify({weekId: week.weekId, weekName: week.weekName, systemId: week.systemId})
                localStorage.setItem("weekId", weekStore)
                this.weekName = week.weekName
                this.weekId = week.weekId
                this.systemId = week.systemId
                this.setSelectedWeek(week)

                // Set early rep level from active weeks response so permissions
                // are available before the full week data loads
                if (week.repLevel != null) {
                    this.repObj = { ...this.repObj, level: week.repLevel }
                }

                // Use fetchWithRetry for getting the week data
                // Service worker will handle progressive loading:
                // - Returns fresh cache immediately if < 3 min old
                // - Returns stale cache immediately while fetching fresh in background
                // - Posts WEEK_DATA_UPDATED message when fresh data arrives
                const compressedData = await fetchWithRetry(
                    async () => {
                        this.retryCount++;
                        const result = await api.get(`/api/week/data/${week.weekId}`, {
                            responseType: 'arraybuffer',
                            headers: {
                                'Accept': 'application/octet-stream'
                            }
                        });

                        if (!result) {
                            throw new Error('No data received from API');
                        }

                        return result;
                    },
                    3, // Number of retries
                    1000 // Base delay in ms
                );

                // Use fetchWithRetry for getting the rep data
                this.repData = await fetchWithRetry(
                    async () => {
                        return api.get(`/api/repData/${week.weekId}`);
                    },
                    3, // Number of retries
                    1000 // Base delay in ms
                );

                // Load permissions from server (non-blocking)
                // This will update permissions in the background if server has newer version
                permissionService.loadFromServer(api.get.bind(api)).catch(err => {
                    console.warn('Failed to load permissions from server:', err)
                })

                // Attempt to decompress with retry on failure (data corruption during transfer)
                let parsedData;
                try {
                    parsedData = await this.decompressData(compressedData);
                    if (!parsedData?.week || !parsedData?.rep) {
                        throw new Error('Invalid data structure')
                    }
                } catch (decompressError) {
                    // If decompression fails, try fetching fresh data and retry once
                    const isRetryable = decompressError.message?.includes('decompress') ||
                                      decompressError.message?.includes('JSON') ||
                                      decompressError.message?.includes('parse') ||
                                      decompressError.message?.includes('EOF');

                    if (isRetryable) {
                        console.warn('Decompression failed, refetching fresh data...', decompressError.message);
                        try {
                            const freshData = await api.get(`/api/week/data/${week.weekId}`, {
                                responseType: 'arraybuffer',
                                headers: {
                                    'Accept': 'application/octet-stream',
                                    'Cache-Control': 'no-cache'
                                }
                            });
                            parsedData = await this.decompressData(freshData);
                            if (!parsedData?.week || !parsedData?.rep) {
                                throw new Error('Invalid data structure after retry')
                            }
                        } catch (retryError) {
                            console.error('Retry also failed:', retryError);
                            throw new Error(`Failed to process data: ${decompressError.message}`);
                        }
                    } else {
                        throw new Error(`Failed to process data: ${decompressError.message}`);
                    }
                }

                this.repObj = parsedData.rep
                this.setWeekData(parsedData.week)
                this.lastDataUpdate = Date.now()

                // Persist repObj to localStorage for PWA background recovery
                this.persistRepObj()
            } catch (error) {
                console.error('Failed to load week data after retries:', error)
                this.error = error.message || 'Failed to load week data'
                throw error
            } finally {
                this.setLoading(false)
            }
        },

        // Set up listener for service worker messages about updated week data
        setupServiceWorkerListener() {
            if (swListenerSetup || typeof navigator === 'undefined' || !navigator.serviceWorker) {
                return
            }

            navigator.serviceWorker.addEventListener('message', async (event) => {
                if (event.data?.type === 'WEEK_DATA_UPDATED') {
                    const { weekId, url } = event.data.payload

                    // Only process if this update is for our current week
                    if (weekId && this.weekId && weekId.toString() === this.weekId.toString()) {
                        console.log('[WeekStore] Received fresh week data notification, refreshing...')

                        try {
                            // Fetch the fresh data from cache (SW just updated it)
                            const compressedData = await api.get(`/api/week/data/${this.weekId}`, {
                                responseType: 'arraybuffer',
                                headers: {
                                    'Accept': 'application/octet-stream'
                                }
                            });

                            if (compressedData) {
                                const parsedData = await this.decompressData(compressedData);
                                if (parsedData?.week && parsedData?.rep) {
                                    this.repObj = parsedData.rep
                                    this.setWeekData(parsedData.week)
                                    this.isStaleData = false
                                    this.lastDataUpdate = Date.now()
                                    this.persistRepObj()
                                    console.log('[WeekStore] Week data refreshed with latest version')
                                }
                            }
                        } catch (error) {
                            console.error('[WeekStore] Failed to process updated week data:', error)
                            // Don't throw - we already have data, this was just an update attempt
                        }
                    }
                }
            })

            swListenerSetup = true
        },
        async checkWeekData() {
            const authStore = useAuthStore();
            const week = JSON.parse(localStorage.getItem('weekId'))

            // Restore repObj from localStorage immediately if missing (PWA background recovery)
            if (!this.repObj) {
                this.restoreRepObj()
            }

            if ((!this.weekData || !this.repObj) && week && !authStore.isLocked) {
                await this.loadWeekData(week)
            }
        },

        // Persist repObj to localStorage for PWA background recovery
        persistRepObj() {
            if (this.repObj) {
                try {
                    localStorage.setItem('repObj', JSON.stringify(this.repObj))
                } catch (e) {
                    console.warn('[WeekStore] Failed to persist repObj:', e)
                }
            }
        },

        // Restore repObj from localStorage (called when store data is lost due to PWA backgrounding)
        restoreRepObj() {
            try {
                const stored = localStorage.getItem('repObj')
                if (stored) {
                    this.repObj = JSON.parse(stored)
                    console.log('[WeekStore] Restored repObj from localStorage')
                }
            } catch (e) {
                console.warn('[WeekStore] Failed to restore repObj:', e)
            }
        },

        // Clear persisted data (call on logout or week change)
        clearPersistedData() {
            localStorage.removeItem('repObj')
            localStorage.removeItem('weekId')
        }
    }
})
